'use client';

import { X } from '@phosphor-icons/react';
import { Eyedropper } from '@phosphor-icons/react/dist/ssr';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { createContext, use, useId } from 'react';
import { HexColorPicker } from 'react-colorful';

// @ts-ignore: next supports importing css modules, so this is fine
import styles from './color-picker.module.css';

import { cn } from '../utils/cn';
import { allColors } from '../utils/colors';
import { Button } from './button';
import { Input } from './input';

interface ColorPickerContextProps {
  color: string;
  onColorChange: (color: string) => void;
}

const ColorPickerContext = createContext<ColorPickerContextProps>({
  color: '',
  onColorChange: () => {},
});

interface ColorPickerProps {
  color?: string;
  defaultColor?: string;
  onColorChange?: (color: string) => void;
  children: React.ReactNode;
}

/**
 * This component is the wrapper around a color picker. It manages the color state and provides it to its children.
 *
 * @example
 * ```tsx
 * <ColorPicker color={color} onColorChange={(color) => setColor(color)}>
 *   <ColorPickerPalette />
 *   <ColorPickerInput placeholder="Select a color" />
 * </ColorPicker>
 * ```
 */
export function ColorPicker(props: ColorPickerProps) {
  const { children, color, defaultColor, onColorChange } = props;

  // This allows the compnent be either controlled or uncontrolled, simlar to a normal input in react
  const [controlledColor, contolledOnColorChange] = useControllableState({
    prop: color,
    defaultProp: defaultColor ?? '',
    onChange: onColorChange,
    caller: 'ColorPicker',
  });

  return (
    <ColorPickerContext.Provider
      value={{
        color: controlledColor,
        onColorChange: contolledOnColorChange,
      }}
    >
      {children}
    </ColorPickerContext.Provider>
  );
}

interface ColorPickerInputProps
  extends Omit<React.ComponentProps<typeof Input>, 'value' | 'onChange'> {}

/**
 * An input that allows the user to input a hex color code. It is built on the `<Input />` component.
 *
 * @remarks
 * The consumer of this component is required to implement any validation of the input value.
 */
export function ColorPickerInput(props: ColorPickerInputProps) {
  const { className, ...rest } = props;

  const context = use(ColorPickerContext);
  if (!context) {
    throw new Error('ColorPickerInput must be used within a ColorPicker');
  }

  const { color, onColorChange } = context;

  return (
    <Input
      data-slot="color-picker-input"
      value={color}
      onChange={(e) => onColorChange(e.target.value)}
      className={cn('font-mono', className)}
      {...rest}
    />
  );
}

interface ColorPickerPaletteProps extends Omit<React.ComponentProps<'div'>, 'color' | 'onChange'> {}

/**
 * A palette that allows the user to select a color from
 *
 * This is built on the `<HexColorPicker />` component from {@link https://github.com/omgovich/react-colorful | react-colorful}
 *
 * @remarks
 * The consumer of this component is required to implement any validation of the input value.
 */
export function ColorPickerPalette(props: ColorPickerPaletteProps) {
  const { className, ...rest } = props;

  const context = use(ColorPickerContext);
  if (!context) {
    throw new Error('ColorPickerPalette must be used within a ColorPicker');
  }

  const { color, onColorChange } = context;

  return (
    <div
      data-slot="color-picker-palette"
      className={cn('h-40', styles.colorPickerPalette, className)}
    >
      <HexColorPicker color={color} onChange={onColorChange} {...rest} />
    </div>
  );
}

interface ColorPickerSwatchesProps extends React.ComponentProps<'div'> {
  /**
   * The direction of the swatches.
   * @default 'vertical'
   */
  orientation?: 'horizontal' | 'vertical';
}

export function ColorPickerSwatches(props: Readonly<ColorPickerSwatchesProps>) {
  const { orientation = 'vertical', className, ...rest } = props;

  const context = use(ColorPickerContext);
  if (!context) {
    throw new Error('ColorPickerSwatches must be used within a ColorPicker');
  }

  const { color, onColorChange } = context;

  const colorsInOrder = Object.values(allColors).flatMap((colors) => [...colors].reverse());

  return (
    // biome-ignore lint/a11y/useSemanticElements: Using a div here for custom styling
    <div
      role="listbox"
      data-slot="color-picker-swatches"
      data-orientation={orientation}
      className={cn(
        'grid gap-0.5',
        'data-[orientation=vertical]:grid-cols-11',
        'data-[orientation=horizontal]:auto-cols-fr data-[orientation=horizontal]:grid-rows-11 data-[orientation=horizontal]:grid-flow-col',
        styles.colorPickerSwatches,
        className,
      )}
      {...rest}
    >
      {colorsInOrder.map((colorSwatch) => (
        <button
          key={colorSwatch.name}
          type="button"
          role="option"
          aria-label={`Select color ${colorSwatch.name}`}
          aria-selected={color === colorSwatch.hex}
          style={{ '--color': colorSwatch.hex }}
          className="bg-(--color) aspect-square rounded-sm aria-selected:outline-2"
          onClick={() => onColorChange(colorSwatch.hex)}
        />
      ))}
    </div>
  );
}

interface ColorPickerClearProps extends React.ComponentProps<typeof Button> {}

/**
 * A button that clears the selected color.
 * It is built on top of the Button component and accepts all props that the Button component accepts.
 */
export function ColorPickerClear(props: ColorPickerClearProps) {
  const { children, onClick, ...rest } = props;

  const context = use(ColorPickerContext);
  if (!context) {
    throw new Error('ColorPickerClear must be used within a ColorPicker');
  }

  const { onColorChange } = context;

  return (
    <Button
      onClick={(e) => {
        onColorChange('');
        onClick?.(e);
      }}
      size="small"
      variant="ghost"
      {...rest}
    >
      <X weight="bold" />
      {children}
    </Button>
  );
}

interface ColorPickerNativeProps extends Omit<React.ComponentProps<typeof Button>, 'asChild'> {}

/**
 * This is a small button that allows triggers the browser-native color picker.
 * It is built on top of the Button component and accepts all props that the Button component accepts.
 */
export function ColorPickerNative(props: Readonly<ColorPickerNativeProps>) {
  const { children, ...rest } = props;

  const context = use(ColorPickerContext);
  if (!context) {
    throw new Error('ColorPickerNative must be used within a ColorPicker');
  }

  const { color, onColorChange } = context;

  const id = useId();

  return (
    <Button variant="outline" icon asChild {...rest}>
      <label htmlFor={id}>
        <Eyedropper weight="bold" />
        <span className="sr-only">Native color picker</span>
        {children}
        <input
          id={id}
          type="color"
          className="sr-only"
          value={color}
          onChange={(e) => onColorChange(e.target.value)}
        />
      </label>
    </Button>
  );
}

interface ColorPickerButtonProps extends Omit<React.ComponentProps<typeof Button>, 'children'> {
  /**
   * The placeholder text to display when no color is selected.
   */
  placeholder?: React.ReactNode;
}

/**
 * A button that previews the selected color. It is built on the `<Button />` component.
 *
 * This is useful together with a popover.
 *
 * @example
 * ```tsx
 * <ColorPicker color={color} onColorChange={(color) => setColor(color)}>
 *   <Popover>
 *     <PopoverTrigger asChild>
 *       <ColorPickerButton variant="outline" placeholder="Select a color" />
 *     </PopoverTrigger>
 *     <PopoverContent className="flex flex-col gap-2">
 *       <ColorPickerPalette />
 *       <ColorPickerInput placeholder="Select a color" />
 *     </PopoverContent>
 *   </Popover>
 * </ColorPicker>
 * ```
 */
export function ColorPickerButton(props: ColorPickerButtonProps) {
  const { className, placeholder, ...rest } = props;

  const context = use(ColorPickerContext);
  if (!context) {
    throw new Error('ColorPickerPalette must be used within a ColorPicker');
  }

  const { color } = context;

  return (
    <Button data-slot="color-picker-button" className={cn('justify-start', className)} {...rest}>
      {color ? (
        <>
          <div
            style={{ '--color': color }}
            className="bg-(--color) size-[1em] aspect-square rounded-sm outline-2 mr-1"
          />
          <span className="font-mono">{color}</span>
        </>
      ) : (
        <span className="opacity-50">{placeholder}</span>
      )}
    </Button>
  );
}
